import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useAccount, useSignMessage } from "wagmi";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { Bars } from "react-loader-spinner";
import { 
  HiArrowRight, 
  HiArrowLeft, 
  HiCheck, 
  HiX, 
  HiSearch, 
  HiChevronDown, 
  HiExternalLink,
  HiSparkles,
  HiAcademicCap,
  HiLocationMarker,
  HiGlobe,
  HiLink,
  HiUser,
  HiBriefcase,
  HiStar,
  HiDocumentText,
  HiCamera
} from "react-icons/hi";
import { IoWallet } from "react-icons/io5";
import { FaFlask, FaBuilding, FaGithub, FaOrcid, FaLinkedin, FaTwitter } from "react-icons/fa";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import notify from "../components/common/notify";
import Container from "../components/common/Container";
import MetaTags from "../components/common/MetaTags";
import CircleStep from "../components/common/CircleStep";
import { accessToken } from "../utils/localData";
import { setIsLoggedIn } from "../redux/slices/generalSlice";
import { VERIFICATION_MESSAGE } from "../Constants";
import { allCountries } from "../data/countries";
import UserPlaceHolderImg from "../assets/img/common/user-placeholder-img.png";
import LinkedWalletsSection from "../components/profile/LinkedWalletsSection";
import WalletDisplay from "../components/common/WalletDisplay";
import useVerifiedEnsProfile from "../hooks/useVerifiedEnsProfile";
import { mergeEnsProfileIntoEmptyFields } from "../utils/ensProfilePrefill";

// Step labels for the stepper
const profileSteps = ["Basic Info", "Details & Links"];

const API_BASE_URL = process.env.REACT_APP_BASE_URL || "";

/**
 * CreateProfile Page - Full Width Modern UI
 * Similar to CreateProject flow with step circles and icons
 */
const CreateProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  const queryParams = new URLSearchParams(location.search);
  const isEdit = queryParams.get("edit") === "true";
  const isEmailSignup = queryParams.get("signup") === "email";
  const isSocialSignup = queryParams.get("social") === "true";
  
  const [step, setStep] = useState(0);  // 0-indexed like CreateProject
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [skillInput, setSkillInput] = useState("");
  const [verifiedAccounts, setVerifiedAccounts] = useState(null);
  const [linkingProvider, setLinkingProvider] = useState(null);
  const linkWindowRef = useRef(null);
  const fileInputRef = useRef(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isNewSignup, setIsNewSignup] = useState(false);
  const [pendingSignupData, setPendingSignupData] = useState(null);
  
  // Wallet linking state
  const { address: walletAddress, isConnected: isWalletConnected } = useAccount();
  const { openConnectModal } = useConnectModal();
  const { signMessageAsync } = useSignMessage();
  const [walletLinked, setWalletLinked] = useState(false);
  const [walletSignature, setWalletSignature] = useState(null);
  const [isSigningWallet, setIsSigningWallet] = useState(false);
  const prevWalletConnected = useRef(false);
  const verifiedEnsProfile = useVerifiedEnsProfile(
    isWalletConnected ? walletAddress : undefined
  );

  // Fetch user data on mount or handle new signup
  useEffect(() => {
    const initializeProfile = async () => {
      const token = accessToken();
      
      // Handle new email signup (no token yet)
      if (isEmailSignup) {
        const pendingData = sessionStorage.getItem("pending_signup");
        if (!pendingData) {
          notify("No signup data found. Please start the registration process again.", "error");
          navigate("/");
          return;
        }
        
        const signupData = JSON.parse(pendingData);
        setPendingSignupData(signupData);
        setIsNewSignup(true);
        
        // Initialize empty form for new user
        setFormData({
          role: "",
          firstname: "",
          lastname: "",
          fundername: "",
          profile_image: null,
          skills: [],
          title_rank: "",
          nationality: "",
          university_college: "",
          location: "",
          description: "",
          biography: "",
          experiences: "",
          accomplishment: "",
          linkedIn: "",
          x: "",
          websites: [""],
        });
        setIsLoading(false);
        return;
      }
      
      // For existing users, require token
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch user");
        }

        const data = await response.json();
        setUser(data.user);
        setVerifiedAccounts(data.user.verified_accounts);
        
        // Determine which step to show
        if (data.user.profile_step >= 2 && !isEdit) {
          navigate("/profile");
          return;
        }
        
        // Check if this is a new Google signup (no role yet)
        if (!data.user.role) {
          setIsNewSignup(true);
        }
        
        // Set initial form data from user
        setFormData({
          role: data.user.role || "",
          firstname: data.user.firstname || "",
          lastname: data.user.lastname || "",
          fundername: data.user.fundername || "",
          profile_image: data.user.images?.profile || null,
          skills: data.user.skills || [],
          title_rank: data.user.title_rank || "",
          nationality: data.user.nationality || "",
          university_college: data.user.university_college || "",
          location: data.user.location || "",
          description: data.user.description || "",
          biography: data.user.biography || "",
          experiences: data.user.experiences || "",
          accomplishment: data.user.accomplishment || "",
          linkedIn: data.user.linkedIn || "",
          x: data.user.x || "",
          websites: data.user.websites?.length > 0 ? data.user.websites : [""],
        });
        
        // Set image preview if user has one
        if (data.user.images?.profile) {
          setImagePreview(data.user.images.profile);
        }
        
        setStep(0);
        setIsLoading(false);
      } catch (err) {
        notify("Session expired. Please log in again.", "error");
        localStorage.removeItem("token");
        navigate("/");
      }
    };

    initializeProfile();
  }, [navigate, isEdit, isEmailSignup]);

  // Apply each verified ENS record only when it arrives and the destination is
  // still empty. The functional update protects user input entered while RPC
  // requests are in flight.
  useEffect(() => {
    if (isLoading || !verifiedEnsProfile.isEnsVerified) return;

    const role = formData.role || user?.role;
    setFormData((current) =>
      mergeEnsProfileIntoEmptyFields(
        current,
        verifiedEnsProfile.profile,
        role
      )
    );
  }, [
    isLoading,
    formData.role,
    user?.role,
    verifiedEnsProfile.isEnsVerified,
    verifiedEnsProfile.profile,
  ]);

  // Function to refresh user data after OAuth linking
  const refreshVerifiedAccounts = async () => {
    const token = accessToken();
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setVerifiedAccounts(data.user.verified_accounts);
      }
    } catch (err) {
      console.error("Failed to refresh user data:", err);
    }
  };

  // Listen for OAuth result from popup window
  useEffect(() => {
    const handleOAuthMessage = async (event) => {
      const { type, success, provider, error } = event.data || {};
      
      if (type !== "oauth_link_result") return;
      
      setLinkingProvider(null);
      
      if (success) {
        notify(`${provider} account linked successfully!`, "success");
        // Refresh user data to get updated verified_accounts
        await refreshVerifiedAccounts();
      } else {
        notify(error || `Failed to link ${provider} account`, "error");
      }
      
      // Close the popup if it's still open
      if (linkWindowRef.current && !linkWindowRef.current.closed) {
        linkWindowRef.current.close();
      }
    };

    window.addEventListener("message", handleOAuthMessage);
    return () => {
      window.removeEventListener("message", handleOAuthMessage);
      // Clean up popup on unmount
      if (linkWindowRef.current && !linkWindowRef.current.closed) {
        linkWindowRef.current.close();
      }
    };
  }, []);
  
  // Monitor popup close as fallback (in case message doesn't arrive)
  useEffect(() => {
    if (!linkingProvider) return;

    const checkPopupClosed = setInterval(async () => {
      if (linkWindowRef.current && linkWindowRef.current.closed) {
        clearInterval(checkPopupClosed);
        // Refresh data when popup closes, even if message wasn't received
        setLinkingProvider(null);
        await refreshVerifiedAccounts();
      }
    }, 500);

    return () => clearInterval(checkPopupClosed);
  }, [linkingProvider]);

  const fetchUserData = async () => {
    const token = accessToken();
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setVerifiedAccounts(data.user.verified_accounts);
        
        // Update formData with latest user data while preserving any unsaved changes
        setFormData(prev => ({
          ...prev,
          // Only update fields that come from the server (like verified_accounts)
          // Don't override fields the user might be editing
          skills: data.user.skills || prev.skills,
          title_rank: data.user.title_rank || prev.title_rank,
          nationality: data.user.nationality || prev.nationality,
          university_college: data.user.university_college || prev.university_college,
        }));
      }
    } catch (err) {
      console.error("Failed to refresh user data:", err);
    }
  };

  // Handle image changes
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        notify("Image must be less than 10MB", "error");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setFormData((prev) => ({ ...prev, profile_image: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  // Handle skill addition
  const addSkill = () => {
    if (skillInput.trim() && formData.skills.length < 10) {
      setFormData((prev) => ({
        ...prev,
        skills: [...prev.skills, { id: Date.now().toString(), text: skillInput.trim() }],
      }));
      setSkillInput("");
      setErrors((prev) => ({ ...prev, skills: null }));
    }
  };

  // Handle skill removal
  const removeSkill = (id) => {
    setFormData((prev) => ({
      ...prev,
      skills: prev.skills.filter((skill) => skill.id !== id),
    }));
  };

  // Handle website changes
  const handleWebsiteChange = (index, value) => {
    const newWebsites = [...formData.websites];
    newWebsites[index] = value;
    setFormData((prev) => ({ ...prev, websites: newWebsites }));
  };

  const addWebsite = () => {
    if (formData.websites.length < 5) {
      setFormData((prev) => ({ ...prev, websites: [...prev.websites, ""] }));
    }
  };

  const removeWebsite = (index) => {
    setFormData((prev) => ({
      ...prev,
      websites: prev.websites.filter((_, i) => i !== index),
    }));
  };

  // Handle nationality selection
  const handleNationalitySelect = (countryName) => {
    setFormData((prev) => ({ ...prev, nationality: countryName }));
    setErrors((prev) => ({ ...prev, nationality: null }));
  };

  // Handle OAuth linking
  const handleLinkAccount = (provider) => {
    const token = accessToken();
    if (!token) {
      notify("Please log in first", "error");
      return;
    }
    
    setLinkingProvider(provider);
    const oauthUrl = `${API_BASE_URL}/auth/link/${provider}?token=${token}&popup=true`;
    
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    
    linkWindowRef.current = window.open(
      oauthUrl,
      `Link ${provider}`,
      `width=${width},height=${height},left=${left},top=${top},popup=yes`
    );
  };

  // Validate step 1
  const validateStep1 = () => {
    const newErrors = {};
    
    // Role is required for new signups
    if (isNewSignup && !formData.role) {
      newErrors.role = "Please select your role";
    }
    
    const role = formData.role || user?.role;
    
    if (role === "applicant") {
      if (!formData.firstname?.trim()) {
        newErrors.firstname = "First name is required";
      }
      if (!formData.lastname?.trim()) {
        newErrors.lastname = "Last name is required";
      }
      if (!formData.skills?.length) {
        newErrors.skills = "Please add at least one skill";
      }
      if (!formData.title_rank?.trim()) {
        newErrors.title_rank = "Title/rank is required";
      }
      if (!formData.university_college?.trim()) {
        newErrors.university_college = "University/company is required";
      }
    } else if (role === "organization") {
      if (!formData.fundername?.trim()) {
        newErrors.fundername = "Organization name is required";
      }
    } else {
      // No role selected yet
      newErrors.role = "Please select your role";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle wallet linking during profile creation
  const handleLinkWallet = async () => {
    if (!isWalletConnected) {
      openConnectModal?.();
      return;
    }
    
    setIsSigningWallet(true);
    try {
      const signature = await signMessageAsync({ message: VERIFICATION_MESSAGE });
      setWalletSignature(signature);
      setWalletLinked(true);
      notify("Wallet verified! It will be linked to your profile.", "success");
    } catch (error) {
      console.error("Wallet signing error:", error);
      if (error.message?.includes("User rejected")) {
        notify("Signature request was rejected", "error");
      } else {
        notify("Failed to verify wallet ownership", "error");
      }
    } finally {
      setIsSigningWallet(false);
    }
  };

  // Auto-trigger wallet signing when wallet connects (better UX — no extra click)
  useEffect(() => {
    if (isWalletConnected && walletAddress && !prevWalletConnected.current && !walletLinked && !isSigningWallet) {
      handleLinkWallet();
    }
    prevWalletConnected.current = isWalletConnected;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWalletConnected, walletAddress]);

  // Unlink wallet during profile creation
  const handleUnlinkWallet = () => {
    setWalletLinked(false);
    setWalletSignature(null);
    notify("Wallet unlinked", "info");
  };

  // Save step 1 - For new signups, just validate and move to step 2
  // For existing users, save to backend
  const saveStep1 = async () => {
    if (!validateStep1()) return;

    setIsSaving(true);
    try {
      // For new email signups, create the user account first so they have a token for OAuth linking
      if (pendingSignupData && !user?._id) {
        const role = formData.role;
        
        const registerBody = {
          email: pendingSignupData.email,
          password: pendingSignupData.password,
          role: role,
          ...(role === "applicant" ? {
            firstname: formData.firstname,
            lastname: formData.lastname,
          } : {
            fundername: formData.fundername,
          }),
          ...(formData.profile_image && { profile_image: formData.profile_image }),
        };

        const registerResponse = await fetch(`${API_BASE_URL}/auth/register`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(registerBody),
        });

        const registerData = await registerResponse.json();

        if (!registerResponse.ok) {
          throw new Error(registerData.message || "Failed to create account");
        }

        // Store the token
        localStorage.setItem("token", registerData.token);
        localStorage.setItem("role", registerData.user.role);
        localStorage.setItem("auth_provider", "email");
        
        // Clear pending signup data
        sessionStorage.removeItem("pending_signup");
        setPendingSignupData(null);
        setUser(registerData.user);
        
        // Now complete step 1 with the new token
        const step1Body = role === "applicant" ? {
          role: role,
          firstname: formData.firstname,
          lastname: formData.lastname,
          skills: formData.skills,
          title_rank: formData.title_rank,
          nationality: formData.nationality,
          university_college: formData.university_college,
          location: formData.location,
          description: formData.description,
          ...(formData.profile_image && { profile_image: formData.profile_image }),
          ...(walletLinked && walletAddress && walletSignature && {
            wallet_address: walletAddress,
            wallet_signature: walletSignature,
          }),
        } : {
          role: role,
          fundername: formData.fundername,
          location: formData.location,
          description: formData.description,
          ...(formData.profile_image && { profile_image: formData.profile_image }),
          ...(walletLinked && walletAddress && walletSignature && {
            wallet_address: walletAddress,
            wallet_signature: walletSignature,
          }),
        };

        const step1Response = await fetch(`${API_BASE_URL}/users/complete-profile/step1`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${registerData.token}`,
          },
          body: JSON.stringify(step1Body),
        });

        const step1Data = await step1Response.json();

        if (!step1Response.ok) {
          throw new Error(step1Data.message || "Failed to save profile");
        }

        setUser(step1Data.user);
        notify("Account created! Now add some optional details.", "success");
        setStep(1);
        return;
      }
      
      // For new Google signups without a user ID, just move to step 2
      if (isNewSignup && !user?._id) {
        setStep(1);
        notify("Great! Now add some optional details.", "success");
        return;
      }
      
      // For Google users with an account but no role, or existing users editing
      const token = accessToken();
      const role = formData.role || user?.role;
      
      const body = role === "applicant" ? {
        role: role,
        firstname: formData.firstname,
        lastname: formData.lastname,
        skills: formData.skills,
        title_rank: formData.title_rank,
        nationality: formData.nationality,
        university_college: formData.university_college,
        location: formData.location,
        description: formData.description,
        ...(formData.profile_image && { profile_image: formData.profile_image }),
        ...(walletLinked && walletAddress && walletSignature && {
          wallet_address: walletAddress,
          wallet_signature: walletSignature,
        }),
      } : {
        role: role,
        fundername: formData.fundername,
        location: formData.location,
        description: formData.description,
        ...(formData.profile_image && { profile_image: formData.profile_image }),
        ...(walletLinked && walletAddress && walletSignature && {
          wallet_address: walletAddress,
          wallet_signature: walletSignature,
        }),
      };

      const response = await fetch(`${API_BASE_URL}/users/complete-profile/step1`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to save profile");
      }

      setUser(data.user);
      notify("Profile saved! Now add some optional details.", "success");
      setStep(1);
    } catch (err) {
      notify(err.message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Save step 2 - Complete the profile with optional details
  const saveStep2 = async (skip = false) => {
    setIsSaving(true);
    try {
      // Get token (should exist now for all users including email signups)
      const token = accessToken();
      
      if (!token) {
        throw new Error("Session expired. Please log in again.");
      }
      
      // Filter out empty websites and empty URLs
      const cleanWebsites = formData.websites.filter((w) => w && w.trim());
      const cleanLinkedIn = formData.linkedIn?.trim() || undefined;
      const cleanX = formData.x?.trim() || undefined;
      
      const body = skip ? {} : {
        biography: formData.biography || undefined,
        experiences: formData.experiences || undefined,
        accomplishment: formData.accomplishment || undefined,
        linkedIn: cleanLinkedIn,
        x: cleanX,
        websites: cleanWebsites.length > 0 ? cleanWebsites : undefined,
        ...(formData.role === "organization" && formData.fundername && { fundername: formData.fundername }),
      };

      // Remove undefined values
      Object.keys(body).forEach(key => body[key] === undefined && delete body[key]);

      const response = await fetch(`${API_BASE_URL}/users/complete-profile/step2`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to save profile");
      }

      localStorage.setItem("role", data.user.role);
      dispatch(setIsLoggedIn(true));
      
      // Check if this was an email signup to show appropriate message
      const wasEmailSignup = localStorage.getItem("auth_provider") === "email";
      
      if (wasEmailSignup) {
        notify("Account created! Please check your email to verify your account.", "success");
      } else {
        notify(skip ? "Profile created! You can add more details later." : "Profile complete!", "success");
      }
      
      navigate("/profile");
    } catch (err) {
      notify(err.message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full min-h-screen flex justify-center items-center">
        <Bars height="50" width="50" color="#8538ff" ariaLabel="bars-loading" visible={true} />
      </div>
    );
  }

  // Determine if user is applicant based on formData.role or user.role
  const isApplicant = (formData.role || user?.role) === "applicant";
  
  // Handle role change in form
  const handleRoleChange = (role) => {
    setFormData(prev => ({ ...prev, role }));
    setErrors(prev => ({ ...prev, role: null }));
  };

  const handleNextStep = () => {
    if (step === 0) {
      saveStep1();
    }
  };

  const handlePrevStep = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleClickStep = (stepIndex) => {
    // Allow going back without validation
    if (stepIndex < step) {
      setStep(stepIndex);
      return;
    }
    
    // For going forward, validate current step first
    if (stepIndex > step) {
      if (step === 0 && validateStep1()) {
        setStep(stepIndex);
      } else if (step === 0) {
        notify("Please complete all required fields in the current step", "warning");
      }
    }
  };

  return (
    <div className="w-full flex flex-col pt-24 mlg:pt-32 items-center roboto-font min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <MetaTags
        title={isEdit ? "Edit Profile" : "Complete Profile"}
        description="Complete your FunDeSci profile to start creating projects and funding research"
        link="/create-profile"
        noIndex
      />
      
      {/* Stepper Header - Like CreateProject */}
      <div className="w-full flex flex-col items-center justify-center pb-8 mb-8 mlg:mb-12 bg-custom-gray-100">
        <Container className="flex justify-between items-center">
          {step > 0 ? (
            <button
              type="button"
              onClick={handlePrevStep}
              className="hidden mlg:flex text-custom-gray-800 font-medium md:text-lg pr-5 group hover:text-primary items-center min-w-fit md:w-36 h-fit"
            >
              <BsChevronLeft className="text-custom-gray-800 mr-1 group-hover:text-primary" />
              Prev Step
            </button>
          ) : (
            <span className="w-24 md:min-w-[144px] hidden mlg:block"></span>
          )}

          {/* Step Circles */}
          <div className="w-full lg:w-9/12 flex items-center justify-center">
            {profileSteps.map((item, index, stepsArray) => (
              <CircleStep
                label={item}
                key={index}
                stepLength={stepsArray.length}
                onClick={() => handleClickStep(index)}
                stepIndex={index}
                activeStep={step}
                circleClass="w-10 h-10 mlg:w-12 mlg:h-12"
                buttonCLass="w-32 mlg:w-40"
              />
            ))}
          </div>

          {step < 1 ? (
            <button
              type="button"
              onClick={handleNextStep}
              disabled={isSaving}
              className="hidden mlg:flex text-custom-gray-800 font-medium md:text-lg pl-5 group hover:text-primary items-center min-w-fit md:w-36 h-fit"
            >
              Next Step
              <BsChevronRight className="text-custom-gray-800 ml-1 group-hover:text-primary" />
            </button>
          ) : (
            <span className="w-24 md:min-w-[144px] hidden mlg:block"></span>
          )}
        </Container>

        {/* Mobile Nav Buttons */}
        <div className="w-full flex mlg:hidden justify-between mt-5 px-7">
          {step > 0 ? (
            <button
              type="button"
              onClick={handlePrevStep}
              className="flex text-custom-gray-800 font-medium group hover:text-primary items-center"
            >
              <BsChevronLeft className="mr-1" />
              Prev Step
            </button>
          ) : (
            <span className="w-24"></span>
          )}
          {step < 1 ? (
            <button
              type="button"
              onClick={handleNextStep}
              disabled={isSaving}
              className="flex text-custom-gray-800 font-medium group hover:text-primary items-center"
            >
              Next Step
              <BsChevronRight className="ml-1" />
            </button>
          ) : (
            <span className="w-24"></span>
          )}
        </div>
      </div>

      {/* Main Content - Wider Layout */}
      <Container className="pb-20">
        <div className="w-full max-w-6xl mx-auto">
          {/* Page Title */}
          <div className="text-center mb-10">
            <div className="inline-flex w-20 h-20 rounded-2xl bg-gradient-to-br from-primary/20 to-primary-green/20 items-center justify-center mb-5 shadow-lg shadow-primary/10">
              {isApplicant ? (
                <FaFlask className="text-primary" size={36} />
              ) : (
                <FaBuilding className="text-primary" size={36} />
              )}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-secondary abel-font mb-3">
              {isEdit ? "Edit Your Profile" : step === 0 ? "Tell us about yourself" : "Add optional details"}
            </h1>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              {step === 0 
                ? "Fill in the required information to get started on FunDeSci" 
                : "Enhance your profile with additional details and linked accounts"}
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
            <div className="p-6 md:p-10 lg:p-12">
              {step === 0 ? (
                <Step1Form
                  isApplicant={isApplicant}
                  formData={formData}
                  errors={errors}
                  handleChange={handleChange}
                  skillInput={skillInput}
                  setSkillInput={setSkillInput}
                  addSkill={addSkill}
                  removeSkill={removeSkill}
                  handleNationalitySelect={handleNationalitySelect}
                  handleImageChange={handleImageChange}
                  imagePreview={imagePreview}
                  fileInputRef={fileInputRef}
                  user={user}
                  isNewSignup={isNewSignup}
                  handleRoleChange={handleRoleChange}
                  // Wallet props
                  walletAddress={walletAddress}
                  isWalletConnected={isWalletConnected}
                  walletLinked={walletLinked}
                  isSigningWallet={isSigningWallet}
                  handleLinkWallet={handleLinkWallet}
                  handleUnlinkWallet={handleUnlinkWallet}
                  verifiedEnsProfile={verifiedEnsProfile}
                />
              ) : (
                <Step2Form
                  isApplicant={isApplicant}
                  formData={formData}
                  handleChange={handleChange}
                  handleWebsiteChange={handleWebsiteChange}
                  addWebsite={addWebsite}
                  removeWebsite={removeWebsite}
                  verifiedAccounts={verifiedAccounts}
                  linkingProvider={linkingProvider}
                  handleLinkAccount={handleLinkAccount}
                  verifiedEnsProfile={verifiedEnsProfile}
                />
              )}
            </div>

            {/* Footer Actions */}
            <div className="px-6 md:px-10 lg:px-12 py-6 bg-gray-50 border-t border-gray-100">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                {step === 1 ? (
                  <button
                    onClick={() => setStep(0)}
                    className="flex items-center gap-2 px-5 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-all"
                  >
                    <HiArrowLeft size={18} />
                    Back to Step 1
                  </button>
                ) : (
                  <div />
                )}
                
                <div className="flex gap-4">
                  {step === 1 && (
                    <button
                      onClick={() => saveStep2(true)}
                      disabled={isSaving}
                      className="px-6 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-all font-medium"
                    >
                      Skip for now
                    </button>
                  )}
                  
                  <button
                    onClick={step === 0 ? saveStep1 : () => saveStep2(false)}
                    disabled={isSaving}
                    className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-primary via-[#8a5cff] to-[#7e7cff] text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-primary/30 hover:scale-[1.02] transition-all disabled:opacity-70 disabled:hover:scale-100"
                  >
                    {isSaving ? (
                      <Bars color="#fff" height={20} width={20} />
                    ) : (
                      <>
                        {step === 0 ? "Continue" : "Complete Profile"}
                        <HiArrowRight size={18} />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

// Country Dropdown Component with Search
const CountryDropdown = ({ value, onChange, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  const filteredCountries = allCountries.filter((country) =>
    country.name.toLowerCase().includes(search.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-5 py-4 rounded-xl border-2 text-left flex items-center justify-between transition-all ${
          error 
            ? "border-red-300 focus:border-red-500" 
            : isOpen 
              ? "border-primary ring-4 ring-primary/10" 
              : "border-white/30 hover:border-white/40"
        } bg-white/50 backdrop-blur-sm text-base`}
      >
        <span className={value ? "text-gray-800" : "text-gray-400"}>
          {value || "Select your nationality"}
        </span>
        <HiChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white/95 backdrop-blur-xl rounded-xl shadow-2xl border border-white/30 overflow-hidden">
          {/* Search Input */}
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search countries..."
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-white/30 bg-white/50 backdrop-blur-sm focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                autoFocus
              />
            </div>
          </div>
          
          {/* Country List */}
          <div className="max-h-64 overflow-y-auto">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => {
                    onChange(country.name);
                    setIsOpen(false);
                    setSearch("");
                  }}
                  className={`w-full px-4 py-3 text-left hover:bg-primary/5 transition-colors flex items-center justify-between ${
                    value === country.name ? "bg-primary/10 text-primary font-medium" : "text-gray-700"
                  }`}
                >
                  <span>{country.name}</span>
                  {value === country.name && <HiCheck className="w-5 h-5 text-primary" />}
                </button>
              ))
            ) : (
              <div className="px-4 py-6 text-center text-gray-500">
                No countries found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Step 1 Form Component
const Step1Form = ({ 
  isApplicant, 
  formData, 
  errors, 
  handleChange,
  skillInput,
  setSkillInput,
  addSkill,
  removeSkill,
  handleNationalitySelect,
  handleImageChange,
  imagePreview,
  fileInputRef,
  user,
  isNewSignup,
  handleRoleChange,
  // Wallet props
  walletAddress,
  isWalletConnected,
  walletLinked,
  isSigningWallet,
  handleLinkWallet,
  handleUnlinkWallet,
  verifiedEnsProfile,
}) => {
  const displayImagePreview = imagePreview || formData.profile_image || UserPlaceHolderImg;
  const showRoleSelection = isNewSignup || !user?.role;
  
  return (
    <div className="space-y-10">
      {/* Role Selection - Only for new signups */}
      {showRoleSelection && (
        <div className="bg-gradient-to-br from-primary/5 via-purple-50/50 to-primary-green/5 rounded-2xl p-6 md:p-8 border border-primary/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-green flex items-center justify-center">
              <HiSparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">I am a... <span className="text-red-500">*</span></h3>
              <p className="text-sm text-gray-500">Select your role to personalize your experience</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => handleRoleChange("applicant")}
              className={`flex flex-col items-center p-6 rounded-xl border-2 transition-all duration-200 ${
                formData.role === "applicant"
                  ? "border-primary bg-white shadow-lg shadow-primary/10"
                  : "border-white/30 bg-white/50 backdrop-blur-sm hover:border-white/40"
              }`}
            >
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-3 transition-all ${
                formData.role === "applicant" ? "bg-primary text-white" : "bg-gray-100 text-gray-500"
              }`}>
                <FaFlask size={24} />
              </div>
              <span className={`text-lg font-bold ${formData.role === "applicant" ? "text-primary" : "text-gray-700"}`}>
                Researcher
              </span>
              <span className="text-gray-500 text-center text-xs mt-1">
                Individual scientist seeking funding
              </span>
              {formData.role === "applicant" && (
                <div className="mt-3 flex items-center gap-1 text-primary text-sm">
                  <HiCheck size={16} />
                  <span className="font-medium">Selected</span>
                </div>
              )}
            </button>

            <button
              type="button"
              onClick={() => handleRoleChange("organization")}
              className={`flex flex-col items-center p-6 rounded-xl border-2 transition-all duration-200 ${
                formData.role === "organization"
                  ? "border-primary bg-white shadow-lg shadow-primary/10"
                  : "border-white/30 bg-white/50 backdrop-blur-sm hover:border-white/40"
              }`}
            >
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-3 transition-all ${
                formData.role === "organization" ? "bg-primary text-white" : "bg-gray-100 text-gray-500"
              }`}>
                <FaBuilding size={24} />
              </div>
              <span className={`text-lg font-bold ${formData.role === "organization" ? "text-primary" : "text-gray-700"}`}>
                Organization
              </span>
              <span className="text-gray-500 text-center text-xs mt-1">
                Lab, institution, DAO, or funder
              </span>
              {formData.role === "organization" && (
                <div className="mt-3 flex items-center gap-1 text-primary text-sm">
                  <HiCheck size={16} />
                  <span className="font-medium">Selected</span>
                </div>
              )}
            </button>
          </div>
          {errors.role && <p className="text-red-500 text-sm mt-3 text-center">{errors.role}</p>}
        </div>
      )}

      {/* Wallet Linking Section - Optional */}
      {(formData.role || user?.role) && (
        <div className="bg-gradient-to-br from-orange-50/50 via-amber-50/30 to-yellow-50/50 rounded-2xl p-6 md:p-8 border border-orange-200/30">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center">
              <IoWallet className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">
                Link Wallet <span className="text-gray-400 text-sm font-normal">(optional)</span>
              </h3>
              <p className="text-sm text-gray-500">
                Connect your wallet to associate past anonymous activity with your profile
              </p>
            </div>
          </div>

          {walletLinked && walletAddress ? (
            <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-green-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <IoWallet className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-800">Wallet Linked</span>
                    <span className="flex items-center gap-1 text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
                      <HiCheck className="w-2.5 h-2.5" />
                      Verified
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 font-mono">
                    <WalletDisplay address={walletAddress} />
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleUnlinkWallet}
                className="px-4 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
              >
                Unlink
              </button>
            </div>
          ) : isWalletConnected && walletAddress ? (
            <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-orange-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <IoWallet className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <span className="font-medium text-gray-800">Wallet Connected</span>
                  <p className="text-sm text-gray-500 font-mono">
                    <WalletDisplay address={walletAddress} />
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleLinkWallet}
                disabled={isSigningWallet}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-400 to-amber-500 text-white rounded-lg hover:shadow-md transition-all text-sm font-medium disabled:opacity-70"
              >
                {isSigningWallet ? (
                  <Bars height="16" width="16" color="#ffffff" ariaLabel="bars-loading" visible={true} />
                ) : (
                  <>
                    <HiLink className="w-4 h-4" />
                    Verify & Link
                  </>
                )}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleLinkWallet}
              className="w-full flex items-center justify-center gap-3 p-4 bg-white rounded-xl border-2 border-dashed border-orange-200 hover:border-orange-400 hover:bg-orange-50/50 transition-all text-gray-600 hover:text-orange-600"
            >
              <IoWallet className="w-5 h-5" />
              <span className="font-medium">Connect Wallet</span>
            </button>
          )}

          <p className="text-xs text-gray-400 mt-3 text-center">
            If you've previously funded projects or joined communities with your wallet, linking it will display your profile instead of anonymous.
          </p>

          {verifiedEnsProfile.isEnsVerified && (
            <div className="mt-4 rounded-xl border border-purple-200 bg-white/80 px-4 py-3">
              <div className="flex items-start gap-2">
                <HiSparkles className="mt-0.5 h-4 w-4 flex-none text-primary" />
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    ENS display name suggestion: {verifiedEnsProfile.ensName}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Safe ENS profile records fill empty fields only. Your legal
                    name and organization name are never inferred from ENS.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Only show rest of form if role is selected (or user already has a role) */}
      {(formData.role || user?.role) && (
        <>
          {/* Avatar Upload Section */}
          <div className="flex flex-col items-center">
            <div className="relative group mb-4">
              <div className="w-32 h-32 rounded-full overflow-hidden ring-4 ring-gray-200 group-hover:ring-primary/40 transition-all shadow-lg">
                <img
                  src={displayImagePreview}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-2 right-2 w-10 h-10 bg-gradient-to-br from-primary to-primary-green hover:from-primary-green hover:to-primary rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
              >
                <HiCamera className="text-white" size={20} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
            </div>
            <p className="text-sm text-gray-500">Click camera icon to upload profile photo</p>
            <p className="text-xs text-gray-400 mt-1">Recommended: Square image, max 10MB</p>
          </div>

          {/* Name Fields (For Applicants) or Organization Name */}
          {isApplicant ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <HiUser className="w-5 h-5 text-primary" />
                  <label className="text-base font-semibold text-gray-700">
                    First Name <span className="text-red-500">*</span>
                  </label>
                </div>
                <input
                  type="text"
                  name="firstname"
                  value={formData.firstname}
                  onChange={handleChange}
                  placeholder="John"
                  className={`w-full px-5 py-4 rounded-xl border-2 transition-all outline-none text-base bg-white/50 backdrop-blur-sm ${
                    errors.firstname 
                      ? "border-red-300 focus:border-red-500" 
                      : "border-white/30 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10"
                  }`}
                />
                {errors.firstname && <p className="text-red-500 text-sm mt-2">{errors.firstname}</p>}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <HiUser className="w-5 h-5 text-primary" />
                  <label className="text-base font-semibold text-gray-700">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                </div>
                <input
                  type="text"
                  name="lastname"
                  value={formData.lastname}
                  onChange={handleChange}
                  placeholder="Doe"
                  className={`w-full px-5 py-4 rounded-xl border-2 transition-all outline-none text-base bg-white/50 backdrop-blur-sm ${
                    errors.lastname 
                      ? "border-red-300 focus:border-red-500" 
                      : "border-white/30 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10"
                  }`}
                />
                {errors.lastname && <p className="text-red-500 text-sm mt-2">{errors.lastname}</p>}
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <FaBuilding className="w-5 h-5 text-primary" />
                <label className="text-base font-semibold text-gray-700">
                  Organization Name <span className="text-red-500">*</span>
                </label>
              </div>
              <input
                type="text"
                name="fundername"
                value={formData.fundername}
                onChange={handleChange}
                placeholder="Your Organization Name"
                className={`w-full px-5 py-4 rounded-xl border-2 transition-all outline-none text-base bg-white/50 backdrop-blur-sm ${
                  errors.fundername 
                    ? "border-red-300 focus:border-red-500" 
                    : "border-white/30 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10"
                }`}
              />
              {errors.fundername && <p className="text-red-500 text-sm mt-2">{errors.fundername}</p>}
            </div>
          )}

          {isApplicant ? (
            <>
              {/* Skills Section - Full Width & Prominent */}
              <div className="bg-gradient-to-br from-primary/5 via-purple-50/50 to-primary-green/5 rounded-2xl p-6 md:p-8 border border-primary/10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-green flex items-center justify-center">
                    <HiSparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">Skills & Expertise <span className="text-red-500">*</span></h3>
                    <p className="text-sm text-gray-500">Add up to 10 skills that define your research expertise</p>
                  </div>
                </div>
                
                <div className="flex gap-3 mb-4">
                  <input
                    type="text"
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addSkill())}
                    placeholder="e.g., Machine Learning, Genomics, Data Analysis, CRISPR..."
                    className="flex-1 px-5 py-4 rounded-xl border-2 border-white/30 bg-white/50 backdrop-blur-sm focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all text-base"
                  />
                  <button
                    type="button"
                    onClick={addSkill}
                    disabled={!skillInput.trim() || formData.skills.length >= 10}
                    className="px-6 py-4 bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add Skill
                  </button>
                </div>
                
                <div className="flex flex-wrap gap-3 min-h-[60px]">
                  {formData.skills?.map((skill) => (
                    <span
                      key={skill.id}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-primary rounded-full text-base font-medium border-2 border-primary/20 shadow-sm"
                    >
                      {skill.text}
                      <button
                        type="button"
                        onClick={() => removeSkill(skill.id)}
                        className="hover:bg-red-100 hover:text-red-500 rounded-full p-1 transition-colors"
                      >
                        <HiX className="w-4 h-4" />
                      </button>
                    </span>
                  ))}
                  {formData.skills?.length === 0 && (
                    <span className="text-gray-400 italic py-2">Your skills will appear here...</span>
                  )}
                </div>
                {errors.skills && <p className="text-red-500 text-sm mt-3">{errors.skills}</p>}
              </div>

              {/* Two Column Grid for Other Fields */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column */}
                <div className="space-y-6">
                  {/* Title/Rank */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <HiAcademicCap className="w-5 h-5 text-primary" />
                      <label className="text-base font-semibold text-gray-700">
                        Title / Rank <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <input
                      type="text"
                      name="title_rank"
                      value={formData.title_rank}
                      onChange={handleChange}
                      placeholder="e.g., PhD Candidate, Principal Investigator, Research Associate"
                      className={`w-full px-5 py-4 rounded-xl border-2 transition-all outline-none text-base bg-white/50 backdrop-blur-sm ${
                        errors.title_rank 
                          ? "border-red-300 focus:border-red-500" 
                          : "border-white/30 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10"
                      }`}
                    />
                    {errors.title_rank && <p className="text-red-500 text-sm mt-2">{errors.title_rank}</p>}
                  </div>

                  {/* University/Company */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <HiBriefcase className="w-5 h-5 text-primary" />
                      <label className="text-base font-semibold text-gray-700">
                        University / Company / Organization <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <input
                      type="text"
                      name="university_college"
                      value={formData.university_college}
                      onChange={handleChange}
                      placeholder="e.g., MIT, Stanford University, Google Research"
                      className={`w-full px-5 py-4 rounded-xl border-2 transition-all outline-none text-base bg-white/50 backdrop-blur-sm ${
                        errors.university_college 
                          ? "border-red-300 focus:border-red-500" 
                          : "border-white/30 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10"
                      }`}
                    />
                    {errors.university_college && <p className="text-red-500 text-sm mt-2">{errors.university_college}</p>}
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  {/* Nationality */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <HiGlobe className="w-5 h-5 text-primary" />
                      <label className="text-base font-semibold text-gray-700">
                        Nationality <span className="text-gray-400 text-sm font-normal">(optional)</span>
                      </label>
                    </div>
                    <CountryDropdown
                      value={formData.nationality}
                      onChange={handleNationalitySelect}
                      error={errors.nationality}
                    />
                  </div>

                  {/* Location */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <HiLocationMarker className="w-5 h-5 text-primary" />
                      <label className="text-base font-semibold text-gray-700">
                        Location <span className="text-gray-400 text-sm font-normal">(optional)</span>
                      </label>
                    </div>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="e.g., Boston, MA, USA"
                      className="w-full px-5 py-4 rounded-xl border-2 border-white/30 bg-white/50 backdrop-blur-sm focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all text-base"
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* Organization Form */
            <div className="space-y-8">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <HiLocationMarker className="w-5 h-5 text-primary" />
                  <label className="text-base font-semibold text-gray-700">Location</label>
                </div>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="e.g., San Francisco, CA, USA"
                  className="w-full px-5 py-4 rounded-xl border-2 border-white/30 bg-white/50 backdrop-blur-sm focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all text-base"
                />
              </div>
              
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <HiDocumentText className="w-5 h-5 text-primary" />
                  <label className="text-base font-semibold text-gray-700">Description</label>
                </div>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={6}
                  placeholder="Tell researchers about your organization, mission, and funding interests..."
                  className="w-full px-5 py-4 rounded-xl border-2 border-white/30 bg-white/50 backdrop-blur-sm focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all resize-none text-base"
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// OAuth Link Card Component
const OAuthLinkCard = ({ provider, name, icon: Icon, color, verified, identifier, suggestion, isLinking, onLink }) => {
  return (
    <div className={`p-5 rounded-xl border-2 transition-all ${
      verified 
        ? "border-green-200 bg-gradient-to-br from-green-50 to-white" 
        : "border-gray-200 bg-white hover:border-primary/30 hover:shadow-md"
    }`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div 
            className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              verified ? "bg-white shadow-sm ring-2 ring-green-100" : "bg-gray-50"
            }`}
            style={{ color: verified ? color : "#9CA3AF" }}
          >
            <Icon className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-800">{name}</span>
              {verified && (
                <span className="flex items-center gap-1 text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
                  <HiCheck className="w-2.5 h-2.5" />
                  Linked
                </span>
              )}
            </div>
            {verified && identifier ? (
              <p className="text-sm text-gray-600">{identifier}</p>
            ) : suggestion ? (
              <>
                <p className="text-sm text-gray-600">@{suggestion}</p>
                <p className="text-xs text-amber-600">
                  ENS suggestion · not verified
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-400">Link your {name} account to verify your identity</p>
            )}
          </div>
        </div>
        
        {!verified && (
          <button
            type="button"
            onClick={onLink}
            disabled={isLinking}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary to-primary-green text-white rounded-lg hover:shadow-lg hover:shadow-primary/30 transition-all text-sm font-medium disabled:opacity-70"
          >
            {isLinking ? (
              <Bars height="16" width="16" color="#ffffff" ariaLabel="bars-loading" visible={true} />
            ) : (
              <>
                <HiExternalLink className="w-4 h-4" />
                Link
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

// Step 2 Form Component
const Step2Form = ({ 
  isApplicant, 
  formData, 
  handleChange,
  handleWebsiteChange,
  addWebsite,
  removeWebsite,
  verifiedAccounts,
  linkingProvider,
  handleLinkAccount,
  verifiedEnsProfile,
}) => {
  return (
    <div className="space-y-10">
      {/* OAuth Linking Section */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-green flex items-center justify-center">
            <HiLink className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800">Link Your Research Accounts</h3>
            <p className="text-sm text-gray-500">Verify your identity by linking external accounts</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <OAuthLinkCard
            provider="github"
            name="GitHub"
            icon={FaGithub}
            color="#333333"
            verified={verifiedAccounts?.github?.verified}
            identifier={verifiedAccounts?.github?.username ? `@${verifiedAccounts.github.username}` : null}
            suggestion={verifiedEnsProfile.profile.githubHandle}
            isLinking={linkingProvider === "github"}
            onLink={() => handleLinkAccount("github")}
          />
          <OAuthLinkCard
            provider="orcid"
            name="ORCID"
            icon={FaOrcid}
            color="#A6CE39"
            verified={verifiedAccounts?.orcid?.verified}
            identifier={verifiedAccounts?.orcid?.orcid_id}
            isLinking={linkingProvider === "orcid"}
            onLink={() => handleLinkAccount("orcid")}
          />
        </div>
      </div>

      {/* Linked Wallets Section */}
      <LinkedWalletsSection />

      <hr className="border-gray-100" />

      {isApplicant ? (
        <>
          {/* Biography - Full Width Large Text Area */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <HiUser className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800">Biography</h3>
                <p className="text-sm text-gray-500">Tell the community about yourself and your research journey</p>
              </div>
            </div>
            <textarea
              name="biography"
              value={formData.biography}
              onChange={handleChange}
              rows={8}
              placeholder="Share your story: your background, research interests, what motivates you, your vision for your field...

You can write as much as you'd like. This is your chance to connect with potential funders and collaborators on a personal level."
              className="w-full px-5 py-4 rounded-xl border-2 border-white/30 bg-white/50 backdrop-blur-sm focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all resize-y min-h-[200px] text-base leading-relaxed"
            />
          </div>

          {/* Research Experience - Full Width Large Text Area */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                <HiBriefcase className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800">Research Experience</h3>
                <p className="text-sm text-gray-500">Detail your research background, labs, and projects</p>
              </div>
            </div>
            <textarea
              name="experiences"
              value={formData.experiences}
              onChange={handleChange}
              rows={8}
              placeholder="Describe your research experience in detail:

• Positions held and labs you've worked in
• Major research projects and your role
• Techniques and methodologies you're proficient in
• Collaborations and team experiences
• Teaching or mentoring experience"
              className="w-full px-5 py-4 rounded-xl border-2 border-white/30 bg-white/50 backdrop-blur-sm focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all resize-y min-h-[200px] text-base leading-relaxed"
            />
          </div>

          {/* Accomplishments - Full Width Large Text Area */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                <HiStar className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800">Key Accomplishments</h3>
                <p className="text-sm text-gray-500">Highlight your achievements, publications, and awards</p>
              </div>
            </div>
            <textarea
              name="accomplishment"
              value={formData.accomplishment}
              onChange={handleChange}
              rows={8}
              placeholder="List your key accomplishments:

• Publications (journals, conferences, preprints)
• Awards and honors
• Grants received
• Patents filed or granted
• Media coverage or notable mentions
• Conference presentations"
              className="w-full px-5 py-4 rounded-xl border-2 border-white/30 bg-white/50 backdrop-blur-sm focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all resize-y min-h-[200px] text-base leading-relaxed"
            />
          </div>
        </>
      ) : (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <HiDocumentText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">Additional Description</h3>
              <p className="text-sm text-gray-500">Share more about your organization's mission and focus</p>
            </div>
          </div>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={8}
            placeholder="Add more details about your organization's funding priorities, areas of interest, and what you look for in research projects..."
            className="w-full px-5 py-4 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all resize-y min-h-[200px] text-base leading-relaxed"
          />
        </div>
      )}

      <hr className="border-gray-100" />

      {/* Social Links */}
      <div>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">
            <HiGlobe className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800">Social & Professional Links</h3>
            <p className="text-sm text-gray-500">Add links to your professional profiles</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <FaLinkedin className="w-4 h-4 text-[#0A66C2]" />
              <label className="text-sm font-semibold text-gray-700">LinkedIn Profile</label>
            </div>
            <input
              type="url"
              name="linkedIn"
              value={formData.linkedIn}
              onChange={handleChange}
              placeholder="https://linkedin.com/in/yourprofile"
              className="w-full px-5 py-4 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all text-base"
            />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <FaTwitter className="w-4 h-4 text-gray-800" />
              <label className="text-sm font-semibold text-gray-700">X (Twitter)</label>
            </div>
            <input
              type="url"
              name="x"
              value={formData.x}
              onChange={handleChange}
              placeholder="https://x.com/yourhandle"
              className="w-full px-5 py-4 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all text-base"
            />
          </div>
        </div>

        {/* Websites */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Personal/Research Websites
          </label>
          <div className="space-y-3">
            {formData.websites?.map((website, index) => (
              <div key={index} className="flex gap-3">
                <input
                  type="url"
                  value={website}
                  onChange={(e) => handleWebsiteChange(index, e.target.value)}
                  placeholder="https://your-website.com"
                  className="flex-1 px-5 py-4 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all text-base"
                />
                {formData.websites.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeWebsite(index)}
                    className="px-4 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                  >
                    <HiX className="w-5 h-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {formData.websites?.length < 5 && (
            <button
              type="button"
              onClick={addWebsite}
              className="mt-3 text-sm text-primary hover:text-primary-dark font-medium flex items-center gap-1"
            >
              <span className="text-lg">+</span> Add another website
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateProfile;
